import React from 'react';
import { Dropdown } from '@insurancemenu/imenu-components-lib';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Dropdown';
import {
  ChevronDown,
  Edit,
  Archive,
  Unarchive,
  Download,
  Description,
  Duplicate,
  InfoButtonFilled,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRFQProductTemplatesStore } from 'store/pages/rfq/rfq-template/rfq-product-templates/pageStore';
import { ProductTemplate, ProductTemplateStatus } from 'types/productServiceV2';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { observer } from 'mobx-react-lite';
import {
  PRODUCT_TEMPLATES_ROW_DROPDOWN_ARCHIVE_TEST_ID,
  PRODUCT_TEMPLATES_ROW_DROPDOWN_DOWNLOAD_PDF_TEST_ID,
  PRODUCT_TEMPLATES_ROW_DROPDOWN_DOWNLOAD_TEMPLATE_TEST_ID,
  PRODUCT_TEMPLATES_ROW_DROPDOWN_DUPLICATE_TEST_ID,
  PRODUCT_TEMPLATES_ROW_DROPDOWN_EDIT_TEST_ID,
  PRODUCT_TEMPLATES_ROW_DROPDOWN_FULL_INFO_TEST_ID,
  PRODUCT_TEMPLATES_ROW_DROPDOWN_BTN_TEST_ID,
  PRODUCT_TEMPLATES_ROW_DROPDOWN_UNARCHIVE_TEST_ID,
} from '../../constants';
import { ActionOptionTextStyled, DropdownButtonStyled } from './styles';

interface MoreDropdownProps {
  productTemplate: ProductTemplate;
  editProductTemplate: () => void;
}
const MoreDropdown: React.FC<MoreDropdownProps> = observer(({ productTemplate, editProductTemplate }) => {
  const {
    downloadGeneratedPdfLink,
    openWarningModal,
    openDuplicateTemplateModal,
    openTemplateInfoModal,
    getTemplateJsonLinkForDownload,
  } = useRFQProductTemplatesStore();

  const isTemplateInProgress =
    productTemplate.status === ProductTemplateStatus.IN_PROGRESS ||
    productTemplate.status === ProductTemplateStatus.INITIAL_SEED;

  const templateInfoDropdownOption = {
    value: 'showTemplateInfo',
    onOptionClick: (): void => {
      openTemplateInfoModal(productTemplate);
    },
    label: (
      <ActionOptionTextStyled>
        <InfoButtonFilled width="2.4rem" />
        Full Info
      </ActionOptionTextStyled>
    ),
    'data-test-id': PRODUCT_TEMPLATES_ROW_DROPDOWN_FULL_INFO_TEST_ID,
  };

  const downloadProductTemplateOption = {
    value: 'downloadProductTemplate',
    label: (
      <ActionOptionTextStyled>
        <Download width="2.4rem" />
        Download Template
      </ActionOptionTextStyled>
    ),
    onOptionClick: async (): Promise<void> => {
      const jsonLink = await getTemplateJsonLinkForDownload(productTemplate.id);
      if (jsonLink) {
        downloadByUrl(jsonLink);
      }
    },
    'data-test-id': PRODUCT_TEMPLATES_ROW_DROPDOWN_DOWNLOAD_TEMPLATE_TEST_ID,
  };

  const downloadGeneratedPdfOption = {
    value: 'downloadGeneratedPdf',
    label: (
      <ActionOptionTextStyled>
        <Download width="2.4rem" />
        Download Generated Pdf
      </ActionOptionTextStyled>
    ),
    onOptionClick: (): void => {
      window.open(downloadGeneratedPdfLink(productTemplate.id, productTemplate.name), '_blank');
    },
    'data-test-id': PRODUCT_TEMPLATES_ROW_DROPDOWN_DOWNLOAD_PDF_TEST_ID,
  };

  const editProductTemplateOption = {
    value: 'editProductTemplate',
    onOptionClick: editProductTemplate,
    label: (
      <ActionOptionTextStyled>
        {isTemplateInProgress ? <Edit width="2.4rem" /> : <Description width="2.4rem" />}
        {isTemplateInProgress ? 'Edit Product' : 'View Product'}
      </ActionOptionTextStyled>
    ),
    'data-test-id': PRODUCT_TEMPLATES_ROW_DROPDOWN_EDIT_TEST_ID,
  };

  const duplicateProductTemplateOption = {
    value: 'duplicateProductTemplate',
    label: (
      <ActionOptionTextStyled>
        <Duplicate width="2.4rem" />
        Duplicate Product
      </ActionOptionTextStyled>
    ),
    onOptionClick: (): void => {
      openDuplicateTemplateModal(productTemplate);
    },
    'data-test-id': PRODUCT_TEMPLATES_ROW_DROPDOWN_DUPLICATE_TEST_ID,
  };

  const archiveProductTemplateOption = {
    value: 'archiveProductTemplate',
    onOptionClick: (): void => {
      openWarningModal(productTemplate, 'ARCHIVE');
    },
    label: (
      <ActionOptionTextStyled>
        <Archive width="2.4rem" />
        Archive Template
      </ActionOptionTextStyled>
    ),
    'data-test-id': PRODUCT_TEMPLATES_ROW_DROPDOWN_ARCHIVE_TEST_ID,
  };

  const unarchiveProductTemplateOption = {
    value: 'unarchiveProductTemplate',
    onOptionClick: (): void => {
      openWarningModal(productTemplate, 'UNARCHIVE');
    },
    label: (
      <ActionOptionTextStyled>
        <Unarchive width="2.4rem" />
        Unarchive Template
      </ActionOptionTextStyled>
    ),
    'data-test-id': PRODUCT_TEMPLATES_ROW_DROPDOWN_UNARCHIVE_TEST_ID,
  };

  const optionsByStatus = (): DropdownOptionType[] => {
    const commonOptions: DropdownOptionType[] = [editProductTemplateOption, templateInfoDropdownOption];

    if (productTemplate.rateSegment === 'CUSTOM') {
      commonOptions.push(duplicateProductTemplateOption);
    }

    switch (productTemplate.status) {
      case ProductTemplateStatus.IN_PROGRESS:
        return [...commonOptions, downloadProductTemplateOption, archiveProductTemplateOption];

      case ProductTemplateStatus.ARCHIVE:
        return [templateInfoDropdownOption, downloadProductTemplateOption, unarchiveProductTemplateOption];

      case ProductTemplateStatus.READY:
      default:
        return [
          ...commonOptions,
          downloadProductTemplateOption,
          downloadGeneratedPdfOption,
          archiveProductTemplateOption,
        ];
    }
  };

  return (
    <Dropdown
      name="More"
      options={optionsByStatus()}
      optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
      optionsWidth="25rem"
      closedElement={
        <DropdownButtonStyled $isClosed color="primary" type="text" rightIconProp={<ChevronDown />}>
          MORE
        </DropdownButtonStyled>
      }
      openedElement={
        <DropdownButtonStyled $isClosed={false} color="primary" rightIconProp={<ChevronDown />}>
          More
        </DropdownButtonStyled>
      }
      data-test-id={PRODUCT_TEMPLATES_ROW_DROPDOWN_BTN_TEST_ID}
    />
  );
});

export default MoreDropdown;
